import React from "react";
import { ArrowRight, MapPin, Sparkles, PlayCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { FirstAidSymbol } from "./FirstAidSymbol";
import { useI18n } from "../lib/i18n";
import { useQr } from "../lib/qrContext";
import { useEmergencyNumber } from "../lib/useEmergencyNumber";

// Streamlined landing page: emergency button on top, optional QR location
// banner, optional Scan&Call promo card (hidden when a qrId is present).
export const Landing = ({ onStart }) => {
    const { t } = useI18n();
    const { qrId, custom } = useQr();
    const { number: effectiveNumber, isCustom } = useEmergencyNumber();

    return (
        <section data-testid="landing-section" className="anim-fade-up py-4">
            {custom?.title && (
                <div
                    data-testid="qr-location-banner"
                    className="mb-5 sm:mb-6 rounded-3xl bg-zinc-900 text-white px-5 sm:px-7 py-5 sm:py-6 shadow-lg"
                >
                    <div className="flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.18em] text-white/80 font-semibold">
                        <MapPin className="w-4 h-4" />
                        {t("qr_landing_location_label")}
                    </div>
                    <div className="mt-1.5 font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight break-words">
                        {custom.title}
                    </div>
                    {custom.location && (
                        <div className="mt-2 text-sm sm:text-base font-bold text-white/95 break-words">
                            {custom.location}
                        </div>
                    )}
                </div>
            )}

            {/* Werksnotruf row – shown ONLY when the QR object supplies a
                custom on-site emergency number. Sits between the location
                banner (if any) and the big red emergency button so the
                site-specific number is impossible to miss. Icon is sized
                to 1rem to feel like a compact inline glyph next to the
                phone number. */}
            {isCustom && (
                <a
                    data-testid="landing-werksnotruf-row"
                    href={`tel:${effectiveNumber}`}
                    className="mb-4 sm:mb-5 w-full flex items-center justify-between gap-3 rounded-2xl bg-white px-4 sm:px-5 py-3 sm:py-3.5 active:scale-[0.99] transition-all focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#0f8040]/25"
                    aria-label={`${t("landing_werksnotruf_label")} ${effectiveNumber}`}
                >
                    <span
                        data-testid="landing-werksnotruf-label"
                        className="font-display font-bold text-sm sm:text-base text-zinc-900 truncate"
                    >
                        {t("landing_werksnotruf_label")}
                    </span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                        <span
                            data-testid="landing-werksnotruf-number"
                            className="font-display font-black text-base sm:text-lg text-[#0f8040] tabular-nums"
                        >
                            {effectiveNumber}
                        </span>
                        <img
                            src="/notruftelefon.png"
                            alt=""
                            aria-hidden
                            className="w-4 h-4 flex-shrink-0"
                        />
                    </span>
                </a>
            )}

            <button
                data-testid="landing-notfall-button"
                onClick={() => onStart("real")}
                className="group relative w-full rounded-3xl bg-[#c70000] text-white p-5 sm:p-7 text-left shadow-[0_20px_60px_-20px_rgba(199,0,0,0.6)] hover:bg-[#a30000] active:scale-[0.98] transition-all focus-visible:ring-4 focus-visible:ring-[#c70000]/30 flex flex-row items-center gap-5 sm:gap-7"
            >
                <FirstAidSymbol
                    size={72}
                    className="drop-shadow-md flex-shrink-0"
                />
                <div className="min-w-0">
                    <div className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none">
                        {t("landing_emergency_title")}
                    </div>
                </div>
            </button>

            {/* Demo card – always shown as a card regardless of id/sponsor
                combination so the entry point looks identical everywhere. */}
            <button
                data-testid="landing-demo-card"
                type="button"
                onClick={() => onStart("demo")}
                className="group mt-5 sm:mt-6 w-full rounded-2xl bg-white border-2 border-zinc-200 hover:border-zinc-900 hover:bg-zinc-900 hover:text-white text-zinc-700 px-4 sm:px-5 py-3.5 sm:py-4 flex items-center gap-3 transition-all text-left"
            >
                <PlayCircle
                    className="w-6 h-6 text-zinc-500 group-hover:text-white flex-shrink-0"
                    strokeWidth={2}
                />
                <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-sm sm:text-base leading-tight">
                        {t("landing_demo_card_title")}
                    </div>
                    <div className="text-xs sm:text-sm text-zinc-500 group-hover:text-white/70 mt-0.5 leading-tight">
                        {t("landing_demo_card_sub")}
                    </div>
                </div>
                <ArrowRight
                    className="w-4 h-4 text-zinc-400 group-hover:text-white flex-shrink-0 transition-transform group-hover:translate-x-0.5"
                    strokeWidth={2.5}
                />
            </button>

            {/* Scan&Call promo card – hidden ONLY when the visitor came in
                via a personalised QR (?id=…). ?sponsor= alone does NOT
                hide it because that visitor is still a potential lead. */}
            {!qrId && <ScanCallPromoCard />}

            {/* Footnote-style disclaimer for the "*" reference in the red
                Notfall button title. Sits at the very bottom of the page
                so the emergency CTA stays visually clean. The trailing
                "Notruf 112" phrase is always the EU number in red –
                never the object-specific override – so the safe fallback
                is always the pan-European 112. */}
            <p
                data-testid="landing-emergency-disclaimer"
                className="mt-6 sm:mt-8 text-center text-[12px] sm:text-[13px] text-zinc-500 leading-relaxed max-w-2xl mx-auto"
            >
                <span
                    aria-hidden
                    className="font-black text-zinc-700 mr-1"
                >
                    *
                </span>
                {(() => {
                    const template = t("landing_disclaimer_full");
                    const [before, after] = template.split("{notruf}");
                    const notrufText = t(
                        "landing_disclaimer_notruf",
                    ).replace("{num}", "112");
                    return (
                        <>
                            {before}
                            <span
                                data-testid="landing-disclaimer-notruf"
                                className="text-[#c70000] font-semibold whitespace-nowrap"
                            >
                                {notrufText}
                            </span>
                            {after}
                        </>
                    );
                })()}
            </p>
        </section>
    );
};

// Promo card – only shown when the user did NOT arrive through a personalised
// QR code. Links to the marketing landing page for Scan&Call + feature tour.
const ScanCallPromoCard = () => {
    const { t } = useI18n();
    return (
        <Link
            data-testid="landing-promo-card"
            to="/landing"
            className="group mt-5 sm:mt-6 block rounded-3xl bg-gradient-to-br from-[#245288] via-[#1c4374] to-[#0f8040] text-white p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <div className="inline-flex items-center gap-2 text-[11px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-white/80 bg-white/15 rounded-full px-3 py-1">
                        <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />
                        {t("promo_card_eyebrow")}
                    </div>
                    <h3 className="mt-3 font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight">
                        {t("promo_card_title")}
                    </h3>
                    <p className="mt-3 text-sm sm:text-base text-white/85 max-w-lg leading-relaxed">
                        {t("promo_card_body")}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 text-sm sm:text-base font-display font-bold text-white group-hover:gap-3 transition-all">
                        {t("promo_card_cta")}
                        <ArrowRight
                            className="w-4 h-4 transition-transform group-hover:translate-x-1"
                            strokeWidth={2.5}
                        />
                    </div>
                </div>
            </div>
        </Link>
    );
};
